import { useState, useCallback, useRef } from 'react';
import { Sparkles, Wand2, FileText, Loader2, CheckCircle, AlertCircle, Search, Trash2, Zap, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import { ai, properties } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface AIToolsProps {
  propertyId: string;
  images: string[]; // R2 keys
  propertyType?: string;
  propertyData?: {
    size_sqm?: number;
    location?: string;
    property_type?: string;
    price?: number;
  };
  onDescriptionGenerated?: (description: string) => void;
}

type ToolStatus = 'idle' | 'loading' | 'success' | 'error';

// Step-by-step progress log
interface LogEntry {
  step: string;
  status: 'pending' | 'running' | 'done' | 'error';
  detail?: string;
}

// Before/after comparison result
interface BeforeAfter {
  imageIndex: number;
  beforeUrl: string;
  afterData: string; // base64
}

// Fetch image from R2 URL and convert to base64
async function fetchImageAsBase64(propertyId: string, key: string): Promise<string> {
  const url = properties.getImageUrl(propertyId, key);
  const response = await fetch(url);
  if (!response.ok) throw new Error('Ekki tókst að sækja mynd');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Get file size in KB from base64
function base64SizeKB(base64: string): number {
  const padding = (base64.match(/=/g) || []).length;
  return Math.round(((base64.length * 3) / 4 - padding) / 1024);
}

export function AITools({
  propertyId,
  images,
  propertyType,
  propertyData,
  onDescriptionGenerated,
}: AIToolsProps) {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [iconStatus, setIconStatus] = useState<ToolStatus>('idle');
  const [descriptionStatus, setDescriptionStatus] = useState<ToolStatus>('idle');
  const [cleaningIndex, setCleaningIndex] = useState<number | null>(null);
  const [reviewStatus, setReviewStatus] = useState<ToolStatus>('idle');
  const [fixAllStatus, setFixAllStatus] = useState<ToolStatus>('idle');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [reviewResults, setReviewResults] = useState<{
    duplicates: number[][];
    quality: { index: number; issue: string }[];
  } | null>(null);

  // Scanning animation state
  const [scanningImageIndex, setScanningImageIndex] = useState<number | null>(null);
  const [scanProgress, setScanProgress] = useState(0);

  // Before/After comparison
  const [beforeAfterList, setBeforeAfterList] = useState<BeforeAfter[]>([]);
  const [compareIndex, setCompareIndex] = useState<number | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const compareRef = useRef<HTMLDivElement>(null);

  const hasImages = images.length > 0;
  const primaryImageKey = images[0];

  // Log management
  const addLog = useCallback((step: string, status: LogEntry['status'] = 'running', detail?: string) => {
    setLogs(prev => {
      const existing = prev.findIndex(l => l.step === step);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { step, status, detail };
        return updated;
      }
      return [...prev, { step, status, detail }];
    });
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  // ═══════════════════════════════════════
  // Generate Icon
  // ═══════════════════════════════════════
  const handleGenerateIcon = async () => {
    if (!token || !primaryImageKey) return;
    setIconStatus('loading');
    clearLogs();
    try {
      addLog('Sæki mynd frá geymslu...', 'running');
      const base64 = await fetchImageAsBase64(propertyId, primaryImageKey);
      addLog('Sæki mynd frá geymslu...', 'done', `${base64SizeKB(base64)} KB`);

      addLog('Sendi til Gemini AI...', 'running', 'Búa til 2D ikon');
      const result = await ai.generateIcon(token, base64, propertyType, propertyId);
      addLog('Sendi til Gemini AI...', 'done');

      if (result.success && result.data.icon_data) {
        addLog('Vista ikon á eign...', 'done');
        setIconStatus('success');
        queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        toast({ title: 'Ikon búið til', description: 'Nýtt ikon hefur verið vistað á eigninni.' });
      } else {
        addLog('Villa', 'error', 'Gemini skilaði engri mynd');
        setIconStatus('error');
      }
    } catch (error) {
      addLog('Villa', 'error', error instanceof Error ? error.message : 'Óþekkt villa');
      setIconStatus('error');
      toast({ title: 'Villa', description: error instanceof Error ? error.message : 'Ekki tókst að búa til ikon.', variant: 'destructive' });
    }
  };

  // ═══════════════════════════════════════
  // Clean Single Photo
  // ═══════════════════════════════════════
  const handleCleanPhoto = async (index: number): Promise<BeforeAfter | null> => {
    if (!token || !images[index]) return null;
    setCleaningIndex(index);
    setScanningImageIndex(index);
    setScanProgress(0);

    // Animate scan progress
    const progressInterval = setInterval(() => {
      setScanProgress(prev => Math.min(prev + 2, 90));
    }, 100);

    try {
      const stepPrefix = `Mynd ${index + 1}`;
      addLog(`${stepPrefix}: Sæki...`, 'running');
      const beforeUrl = properties.getImageUrl(propertyId, images[index]);
      const base64 = await fetchImageAsBase64(propertyId, images[index]);
      addLog(`${stepPrefix}: Sæki...`, 'done', `${base64SizeKB(base64)} KB`);

      addLog(`${stepPrefix}: AI hreinsar...`, 'running', 'Fjarlægi persónulega hluti');
      const result = await ai.cleanPhoto(token, base64);

      clearInterval(progressInterval);
      setScanProgress(100);

      if (result.success && result.data.photo_data) {
        // Log detected and removed objects
        const detected = result.data.detected_objects || [];
        const removed = result.data.removed_objects || [];
        if (detected.length > 0) {
          addLog(`${stepPrefix}: Greint`, 'done', detected.join(', '));
        }
        if (removed.length > 0) {
          addLog(`${stepPrefix}: Fjarlægt`, 'done', removed.join(', '));
        } else if (detected.length === 0) {
          addLog(`${stepPrefix}: Ekkert fjarlægt`, 'done', 'Engin persónuleg gögn');
        }
        addLog(`${stepPrefix}: AI hreinsar...`, 'done');

        addLog(`${stepPrefix}: Hleð upp...`, 'running');
        const cleanedBase64 = result.data.photo_data.replace(/^data:image\/\w+;base64,/, '');
        const binaryStr = atob(cleanedBase64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        const file = new File([blob], `cleaned-${Date.now()}.jpg`, { type: 'image/jpeg' });
        await properties.uploadImage(token, propertyId, file);
        addLog(`${stepPrefix}: Hleð upp...`, 'done');

        addLog(`${stepPrefix}: Eyði gamalli...`, 'running');
        await properties.deleteImage(token, propertyId, images[index]);
        addLog(`${stepPrefix}: Eyði gamalli...`, 'done');

        const ba: BeforeAfter = { imageIndex: index, beforeUrl, afterData: result.data.photo_data };
        return ba;
      } else {
        addLog(`${stepPrefix}: Villa`, 'error', 'Gemini skilaði engri mynd');
        return null;
      }
    } catch (error) {
      clearInterval(progressInterval);
      addLog(`Mynd ${index + 1}: Villa`, 'error', error instanceof Error ? error.message : 'Óþekkt villa');
      return null;
    } finally {
      setCleaningIndex(null);
      setScanningImageIndex(null);
      setScanProgress(0);
    }
  };

  // ═══════════════════════════════════════
  // Clean single image (button handler)
  // ═══════════════════════════════════════
  const handleCleanSingle = async (index: number) => {
    clearLogs();
    const ba = await handleCleanPhoto(index);
    if (ba) {
      setBeforeAfterList([ba]);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      toast({ title: 'Mynd hreinsuð', description: 'Persónulegir hlutir hafa verið fjarlægðir.' });
    } else {
      toast({ title: 'Villa', description: 'Ekki tókst að hreinsa mynd.', variant: 'destructive' });
    }
  };

  // ═══════════════════════════════════════
  // Generate Description
  // ═══════════════════════════════════════
  const handleGenerateDescription = async () => {
    if (!token || !primaryImageKey) return;
    setDescriptionStatus('loading');
    clearLogs();
    try {
      addLog('Sæki mynd...', 'running');
      const base64 = await fetchImageAsBase64(propertyId, primaryImageKey);
      addLog('Sæki mynd...', 'done', `${base64SizeKB(base64)} KB`);

      addLog('Gemini skrifar lýsingu...', 'running', 'Greining á mynd og eiginleikum');
      const result = await ai.describeProperty(token, base64, propertyData);

      if (result.success && result.data) {
        addLog('Gemini skrifar lýsingu...', 'done');
        addLog('Stafsetning athuguð (Yfirlestur.is)...', 'done');
        onDescriptionGenerated?.(result.data.description);
        setDescriptionStatus('success');
        toast({ title: 'Lýsing búin til', description: 'AI lýsing búin til og stafsetning athuguð.' });
      } else {
        addLog('Villa', 'error', 'Engin lýsing frá Gemini');
        setDescriptionStatus('error');
      }
    } catch (error) {
      addLog('Villa', 'error', error instanceof Error ? error.message : 'Óþekkt villa');
      setDescriptionStatus('error');
      toast({ title: 'Villa', description: error instanceof Error ? error.message : 'Ekki tókst að búa til lýsingu.', variant: 'destructive' });
    }
  };

  // ═══════════════════════════════════════
  // ⚡ FIX ALL AND SAVE — Clean all images + generate description
  // Persistent progress toast + auto-refresh on completion
  // ═══════════════════════════════════════
  const handleFixAll = async () => {
    if (!token || images.length === 0) return;
    setFixAllStatus('loading');
    clearLogs();
    setBeforeAfterList([]);

    const totalImages = images.length;
    const totalSteps = totalImages + 1; // images + description
    addLog('⚡ Laga allt og vista', 'running', `${totalSteps} skref`);

    // Show persistent progress toast that updates in real-time
    const progressToast = toast({
      title: '⚡ AI vinnur...',
      description: `0/${totalImages} myndir hreinsaðar...`,
    });

    const baResults: BeforeAfter[] = [];
    let cleaned = 0;
    let failed = 0;

    // 1) Clean each image
    for (let i = 0; i < images.length; i++) {
      addLog(`━━ Mynd ${i + 1}/${totalImages} ━━`, 'running');
      const ba = await handleCleanPhoto(i);
      if (ba) {
        baResults.push(ba);
        cleaned++;
        addLog(`━━ Mynd ${i + 1}/${totalImages} ━━`, 'done', '✓ Hreinsuð');
      } else {
        failed++;
        addLog(`━━ Mynd ${i + 1}/${totalImages} ━━`, 'error', 'Tókst ekki');
      }

      // Update progress toast in real-time
      progressToast.update({
        id: progressToast.id,
        title: '⚡ AI vinnur...',
        description: `${cleaned + failed}/${totalImages} myndir — ${cleaned} hreinsaðar${failed > 0 ? `, ${failed} mistókust` : ''}`,
      });
    }

    // 2) Generate description from first image
    progressToast.update({
      id: progressToast.id,
      title: '⚡ AI vinnur...',
      description: 'Búa til lýsingu...',
    });

    addLog('━━ Búa til lýsingu ━━', 'running');
    try {
      // Invalidate first so we get updated images
      await queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });

      // Fetch updated property to get new first image
      const updatedProp = await properties.get(token, propertyId);
      const newFirstImage = updatedProp.images?.[0];

      if (newFirstImage) {
        const base64 = await fetchImageAsBase64(propertyId, newFirstImage);
        const result = await ai.describeProperty(token, base64, propertyData);
        if (result.success && result.data) {
          onDescriptionGenerated?.(result.data.description);
          addLog('━━ Búa til lýsingu ━━', 'done', '✓ Lýsing og stafsetning');
        } else {
          addLog('━━ Búa til lýsingu ━━', 'error', 'Engin lýsing');
        }
      } else {
        addLog('━━ Búa til lýsingu ━━', 'error', 'Engin mynd til að lýsa');
      }
    } catch {
      addLog('━━ Búa til lýsingu ━━', 'error', 'Villa við lýsingu');
    }

    setBeforeAfterList(baResults);
    setFixAllStatus(failed === totalImages ? 'error' : 'success');

    // Batch invalidation — auto-refresh gallery and all property views
    await queryClient.invalidateQueries({ queryKey: ['properties'] });
    await queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });

    addLog('⚡ Laga allt og vista', 'done', `${cleaned} hreinsaðar, ${failed} mistókust`);

    // Dismiss progress toast and show final result
    progressToast.dismiss();

    if (failed === 0) {
      toast({
        title: 'Allt lagað og vistað',
        description: `${cleaned} myndir hreinsaðar, lýsing búin til.`,
      });
    } else if (cleaned > 0) {
      toast({
        title: 'Að mestu lagað',
        description: `${cleaned}/${totalImages} myndir hreinsaðar, ${failed} mistókust.`,
      });
    } else {
      toast({
        title: 'Villa',
        description: 'Ekki tókst að hreinsa neinar myndir.',
        variant: 'destructive',
      });
    }
  };

  // ═══════════════════════════════════════
  // Review all images
  // ═══════════════════════════════════════
  const handleReviewImages = async () => {
    if (!token || images.length === 0) return;
    setReviewStatus('loading');
    setReviewResults(null);
    clearLogs();

    try {
      addLog('Sæki allar myndir...', 'running', `${images.length} myndir`);
      const imageData: { index: number; base64: string; sizeKB: number }[] = [];

      for (let i = 0; i < images.length; i++) {
        setScanningImageIndex(i);
        setScanProgress((i / images.length) * 100);
        addLog(`Sæki mynd ${i + 1}/${images.length}...`, 'running');
        try {
          const base64 = await fetchImageAsBase64(propertyId, images[i]);
          const sizeKB = base64SizeKB(base64);
          imageData.push({ index: i, base64, sizeKB });
          addLog(`Sæki mynd ${i + 1}/${images.length}...`, 'done', `${sizeKB} KB`);
        } catch {
          addLog(`Sæki mynd ${i + 1}/${images.length}...`, 'error', 'Ekki tókst');
        }
      }

      setScanningImageIndex(null);
      setScanProgress(0);
      addLog('Leita að tvítekningum...', 'running');

      // Find duplicates by fingerprint
      const duplicates: number[][] = [];
      const seen = new Map<string, number>();
      for (const img of imageData) {
        const fingerprint = img.base64.slice(-500);
        const prev = seen.get(fingerprint);
        if (prev !== undefined) {
          const existingGroup = duplicates.find(g => g.includes(prev));
          if (existingGroup) existingGroup.push(img.index);
          else duplicates.push([prev, img.index]);
        } else {
          seen.set(fingerprint, img.index);
        }
      }

      addLog('Leita að tvítekningum...', 'done',
        duplicates.length > 0 ? `${duplicates.length} tvítekningar` : 'Engar tvítekningar');

      addLog('Athuga gæði mynda...', 'running');
      const quality: { index: number; issue: string }[] = [];
      for (const img of imageData) {
        if (img.sizeKB < 20) quality.push({ index: img.index, issue: 'Mjög lítil mynd (< 20 KB)' });
        if (img.sizeKB > 8000) quality.push({ index: img.index, issue: 'Mjög stór mynd (> 8 MB)' });
      }
      addLog('Athuga gæði mynda...', 'done',
        quality.length > 0 ? `${quality.length} athugasemdir` : 'Allt í lagi');

      setReviewResults({ duplicates, quality });
      setReviewStatus('success');
      toast({ title: 'Yfirferð lokið', description: `${images.length} myndir skoðaðar.` });
    } catch (error) {
      addLog('Villa', 'error', error instanceof Error ? error.message : 'Óþekkt villa');
      setReviewStatus('error');
    }
  };

  // Delete a duplicate image
  const handleDeleteDuplicate = async (imageIndex: number) => {
    if (!token) return;
    try {
      await properties.deleteImage(token, propertyId, images[imageIndex]);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      toast({ title: 'Mynd eytt', description: `Mynd ${imageIndex + 1} fjarlægð.` });
      setReviewResults(null);
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að eyða mynd.', variant: 'destructive' });
    }
  };

  // Before/After slider handler
  const handleSliderMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!compareRef.current) return;
    const rect = compareRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPosition((x / rect.width) * 100);
  };

  const isAnyLoading = iconStatus === 'loading' || descriptionStatus === 'loading' ||
    cleaningIndex !== null || reviewStatus === 'loading' || fixAllStatus === 'loading';

  if (!hasImages) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-500" />
        AI verkfæri
      </h4>

      {/* ═══ Main Action Buttons ═══ */}
      <div className="flex flex-wrap gap-2">
        {/* ⚡ Fix All and Save */}
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleFixAll}
          disabled={isAnyLoading}
          className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md"
        >
          {fixAllStatus === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          Laga allt og vista
          {fixAllStatus === 'success' && <CheckCircle className="h-3.5 w-3.5" />}
          {fixAllStatus === 'error' && <AlertCircle className="h-3.5 w-3.5" />}
        </Button>

        {/* Generate Icon */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateIcon}
          disabled={isAnyLoading}
          className="gap-2"
        >
          {iconStatus === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Búa til ikon
          {iconStatus === 'success' && <CheckCircle className="h-3.5 w-3.5 text-green-600" />}
        </Button>

        {/* Generate Description */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateDescription}
          disabled={isAnyLoading}
          className="gap-2"
        >
          {descriptionStatus === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Búa til lýsingu
          {descriptionStatus === 'success' && <CheckCircle className="h-3.5 w-3.5 text-green-600" />}
        </Button>

        {/* Review Images */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleReviewImages}
          disabled={isAnyLoading}
          className="gap-2"
        >
          {reviewStatus === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Yfirfara ({images.length})
          {reviewStatus === 'success' && <CheckCircle className="h-3.5 w-3.5 text-green-600" />}
        </Button>
      </div>

      {/* ═══ Per-image clean buttons ═══ */}
      <div className="space-y-1.5">
        <p className="text-xs text-gray-500">Hreinsa stakar myndir:</p>
        <div className="flex flex-wrap gap-1.5">
          {images.map((key, index) => (
            <Button
              key={key}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleCleanSingle(index)}
              disabled={isAnyLoading}
              className="h-7 text-xs gap-1 relative overflow-hidden"
            >
              {/* Scanning animation overlay */}
              {scanningImageIndex === index && (
                <div
                  className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-blue-400/40 to-blue-400/20 animate-pulse"
                  style={{ width: `${scanProgress}%` }}
                />
              )}
              {cleaningIndex === index ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Wand2 className="h-3 w-3" />
              )}
              Mynd {index + 1}
            </Button>
          ))}
        </div>
      </div>

      {/* ═══ Scanning Animation (when processing) ═══ */}
      {scanningImageIndex !== null && (
        <div className="relative rounded-lg overflow-hidden border-2 border-blue-400 bg-black">
          <img
            src={properties.getImageUrl(propertyId, images[scanningImageIndex])}
            alt={`Skanna mynd ${scanningImageIndex + 1}`}
            className="w-full h-48 object-cover opacity-80"
          />
          {/* Scanning line animation */}
          <div
            className="absolute left-0 right-0 h-0.5 bg-blue-400 shadow-[0_0_10px_2px_rgba(59,130,246,0.6)] transition-all duration-100"
            style={{ top: `${scanProgress}%` }}
          />
          {/* Scanning overlay with grid effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-blue-500/10 pointer-events-none" />
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <span className="text-xs text-blue-300 font-mono bg-black/60 px-2 py-1 rounded">
              SKÖNNUN... {Math.round(scanProgress)}%
            </span>
            <span className="text-xs text-blue-300 font-mono bg-black/60 px-2 py-1 rounded">
              Mynd {scanningImageIndex + 1}/{images.length}
            </span>
          </div>
        </div>
      )}

      {/* ═══ Live Progress Log ═══ */}
      {logs.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-600">Framvinda:</p>
            <button
              onClick={clearLogs}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Hreinsa
            </button>
          </div>
          {logs.map((log, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {log.status === 'running' && <Loader2 className="h-3 w-3 animate-spin text-blue-500 flex-shrink-0" />}
              {log.status === 'done' && <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />}
              {log.status === 'error' && <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
              {log.status === 'pending' && <div className="h-3 w-3 rounded-full border border-gray-300 flex-shrink-0" />}
              <span className={log.status === 'error' ? 'text-red-700' : 'text-gray-700'}>{log.step}</span>
              {log.detail && <span className="text-gray-400 ml-auto text-right">{log.detail}</span>}
            </div>
          ))}
        </div>
      )}

      {/* ═══ Before/After Comparison ═══ */}
      {beforeAfterList.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-green-800 flex items-center gap-1">
              <ZoomIn className="h-3.5 w-3.5" />
              Fyrir / Eftir samanburður
            </p>
            <button
              onClick={() => { setBeforeAfterList([]); setCompareIndex(null); }}
              className="text-xs text-green-600 hover:text-green-800"
            >
              Loka
            </button>
          </div>

          {/* Thumbnail row */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {beforeAfterList.map((ba, i) => (
              <button
                key={i}
                onClick={() => { setCompareIndex(i); setSliderPosition(50); }}
                className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                  compareIndex === i ? 'border-green-500 scale-105' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <img src={ba.afterData} alt={`Eftir ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Slider comparison view */}
          {compareIndex !== null && beforeAfterList[compareIndex] && (
            <div
              ref={compareRef}
              className="relative w-full h-64 rounded-lg overflow-hidden cursor-col-resize select-none border border-gray-300"
              onMouseMove={handleSliderMove}
              onTouchMove={handleSliderMove}
            >
              {/* After (full background) */}
              <img
                src={beforeAfterList[compareIndex].afterData}
                alt="Eftir"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Before (clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src={beforeAfterList[compareIndex].beforeUrl}
                  alt="Fyrir"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ width: compareRef.current ? `${compareRef.current.offsetWidth}px` : '100%' }}
                />
              </div>
              {/* Slider handle */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center">
                  <span className="text-gray-500 text-xs font-bold">⟷</span>
                </div>
              </div>
              {/* Labels */}
              <div className="absolute top-2 left-2 px-2 py-1 bg-red-500/80 text-white text-xs rounded font-medium">
                FYRIR
              </div>
              <div className="absolute top-2 right-2 px-2 py-1 bg-green-500/80 text-white text-xs rounded font-medium">
                EFTIR
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ Review Results ═══ */}
      {reviewResults && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3">
          <p className="text-xs font-medium text-blue-800">Niðurstöður yfirferðar:</p>

          {reviewResults.duplicates.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-red-700 font-medium">Tvítekningar:</p>
              {reviewResults.duplicates.map((group, gi) => (
                <div key={gi} className="flex items-center gap-2 text-xs bg-red-50 rounded p-2">
                  <span className="text-red-700">
                    Myndir {group.map(i => i + 1).join(' og ')} eru eins
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDuplicate(group[group.length - 1])}
                    className="h-6 text-xs gap-1 ml-auto text-red-600 hover:text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="h-3 w-3" />
                    Eyða mynd {group[group.length - 1] + 1}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-green-700">Engar tvítekningar.</p>
          )}

          {reviewResults.quality.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-amber-700 font-medium">Gæðaathugasemdir:</p>
              {reviewResults.quality.map((q, qi) => (
                <p key={qi} className="text-xs text-amber-600">
                  Mynd {q.index + 1}: {q.issue}
                </p>
              ))}
            </div>
          )}

          {reviewResults.duplicates.length === 0 && reviewResults.quality.length === 0 && (
            <p className="text-xs text-green-700">Allt í lagi! Engar tvítekningar eða gæðavandamál.</p>
          )}
        </div>
      )}
    </div>
  );
}
